import { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useListOrganizationSubtreeQuery } from "~/shared/api/organization/organization.grpc";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";

/**
 * The legal entities of the active company's tree (those with a BIN): who signs a contract as
 * the waste operator. The active company is the default, and the only option while none is known.
 */
export const useWasteOperators = () => {
  const { selectedCompanyId, selectedCompany } = useSelectedCompany();
  const companyId = selectedCompanyId ? String(selectedCompanyId) : "";
  const { currentData, isFetching, isError } = useListOrganizationSubtreeQuery(
    companyId ? { organizationId: BigInt(companyId) } : skipToken,
  );
  const options = useMemo(() => {
    const legal = (currentData?.organizations ?? [])
      .filter((org) => org.bin)
      .map((org) => ({ id: String(org.id), name: org.name, secondary: org.bin }));
    // No legal entity known (loading, or none in the tree): the active company stays pickable.
    return legal.length > 0 || !companyId
      ? legal
      : [{ id: companyId, name: selectedCompany?.name ?? companyId }];
  }, [currentData, companyId, selectedCompany]);
  // None until the tree is in, so a contract list doesn't load for one operator and flip to another.
  const ready = !companyId || !!currentData || isError;
  const defaultId = !ready
    ? ""
    : options.some((o) => o.id === companyId)
      ? companyId
      : options[0]?.id || companyId;
  return { options, defaultId, ready, loading: isFetching };
};
